export class EdgeFunctionError extends Error {
  code: string
  details?: Record<string, unknown>

  constructor(message: string, code: string, details?: Record<string, unknown>) {
    super(message)
    this.name = 'EdgeFunctionError'
    this.code = code
    this.details = details
  }
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || ''
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || ''

/**
 * Invoke a Supabase Edge Function with typed response.
 * Uses direct fetch() for reliable GET/POST support with query params.
 */
export async function invokeEdgeFunction<T>(
  functionName: string,
  options?: {
    body?: Record<string, unknown>
    method?: 'GET' | 'POST'
    params?: Record<string, string>
  }
): Promise<T> {
  const { body, method = 'GET', params } = options ?? {}

  // Build the full URL
  let url = `${SUPABASE_URL}/functions/v1/${functionName}`
  if (params && Object.keys(params).length > 0) {
    const searchParams = new URLSearchParams(params)
    url += `?${searchParams.toString()}`
  }

  const headers: Record<string, string> = {
    'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
    'Content-Type': 'application/json',
  }

  const fetchOptions: RequestInit = {
    method: method || (body ? 'POST' : 'GET'),
    headers,
  }

  if (body && method !== 'GET') {
    fetchOptions.body = JSON.stringify(body)
  }

  const response = await fetch(url, fetchOptions)

  if (!response.ok) {
    const text = await response.text()
    let errorData: any
    try { errorData = JSON.parse(text) } catch { errorData = { error: text } }
    throw new EdgeFunctionError(
      errorData.error || errorData.message || `Edge Function returned ${response.status}`,
      errorData.code || 'EDGE_FUNCTION_ERROR',
      { functionName, status: response.status }
    )
  }

  const data = await response.json()

  // Check for error responses from the function itself
  if (data && typeof data === 'object' && 'error' in data && !('currentPrice' in data)) {
    throw new EdgeFunctionError(
      data.error,
      data.code || 'FUNCTION_ERROR',
      data.details
    )
  }

  return data as T
}
